import { useMemo, useRef, useState } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { withFileAuth } from '@/utils/api';
import { STATE_META, STATE_ORDER, money, fuseGroups } from '@/components/tables/tableStates';
import { TableRowsGrid } from '@/components/tables/TableRowsGrid';
import { FloorUnit } from './FloorUnit';
import { FloorDecor, ZoneTag } from './FloorDecor';
import { isDecor, fmtElapsed } from '../shared';

const ROWS_THRESHOLD = 30;
const DEFAULT_TEXTURE = { beach: 'arena', terrace: 'ceramica', outdoor: 'cesped' };

export const spaceBackground = (space, decor) => {
  const fondo = decor.find(d => d.unit_type === 'fondo' && d.space_id === space?.space_id);
  if (fondo) return { texture: fondo.name };
  if (space?.map_image_url) return { image: space.map_image_url };
  return { texture: DEFAULT_TEXTURE[space?.space_type] || 'madera' };
};

export const ViewSwitch = ({ view, setView, many }) => (
  <div className="inline-flex rounded-lg bg-white/10 p-1 gap-1" role="tablist" data-testid="floor-view-switch">
    {[['real', 'Realista'], ['simple', 'Simple'], [many ? 'rows' : 'list', many ? 'Filas' : 'Lista + plano']].map(([k, l]) => (
      <button key={k} role="tab" aria-selected={view === k} onClick={() => setView(k)} data-testid={`floor-view-${k}`}
        className={`px-3 h-8 rounded-md text-sm font-semibold transition-colors ${view === k ? 'bg-white text-[#111827] shadow' : 'text-white/85 hover:bg-white/10'}`}>{l}</button>
    ))}
  </div>
);

export const FloorLegend = () => (
  <div className="flex flex-wrap gap-x-5 gap-y-1 text-sm" data-testid="floor-legend">
    {STATE_ORDER.map(s => (
      <span key={s} className="inline-flex items-center gap-1.5">
        <span className="w-3 h-3 rounded-full" style={{ background: STATE_META[s].color }} />{STATE_META[s].label}
      </span>
    ))}
  </div>
);

const SpaceCanvas = ({ space, units, decor, simple, now, selectedId, onSelect, onDoubleClick, editable, onMove, onPickDecor, pickedDecor, showTag }) => {
  const ref = useRef(null);
  const drag = useRef(null);
  const bg = spaceBackground(space, decor);
  const drop = (e) => {
    e.preventDefault();
    if (!drag.current || !ref.current) return;
    const r = ref.current.getBoundingClientRect();
    const x = Math.min(98, Math.max(2, ((e.clientX - r.left) / r.width) * 100));
    const y = Math.min(98, Math.max(2, ((e.clientY - r.top) / r.height) * 100));
    onMove?.(drag.current, x, y);
    drag.current = null;
  };
  const style = simple ? {} : bg.image ? { backgroundImage: `url(${withFileAuth(bg.image)})`, backgroundSize: 'cover', backgroundPosition: 'center' } : {};
  return (
    <div ref={ref} onDragOver={(e) => e.preventDefault()} onDrop={drop} style={style}
      className={`relative flex-1 min-w-[320px] min-h-[520px] overflow-hidden ${simple ? 'mfloor-simple' : bg.texture ? `mtex-${bg.texture}` : ''} ${editable ? 'outline-dashed outline-2 outline-[#2563EB]' : ''}`}
      data-testid={`floor-space-${space?.space_id || 'all'}`}>
      {showTag && <ZoneTag className="absolute top-3 left-3 z-10">{space?.name}</ZoneTag>}
      {!simple && decor.filter(d => d.unit_type !== 'fondo').map(d => (
        <FloorDecor key={d.unit_id} item={d} draggable={editable} selected={pickedDecor === d.unit_id}
          onClick={() => onPickDecor?.(d)} onDragStart={() => { drag.current = d.unit_id; }} />
      ))}
      {units.map(u => (
        <FloorUnit key={u.unit_id} unit={u} now={now} simple={simple} selected={selectedId === u.unit_id}
          draggable={editable} onDragStart={() => { drag.current = u.unit_id; }}
          onClick={() => onSelect?.(u)} onDoubleClick={() => onDoubleClick?.(u)} />
      ))}
    </div>
  );
};

const UnitList = ({ units, spaces, selectedId, onSelect, now, showAmount }) => {
  const area = (id) => spaces.find(s => s.space_id === id)?.name || '';
  return (
    <div className="w-[340px] shrink-0 bg-card text-card-foreground border-r border-border overflow-auto" data-testid="floor-list">
      <table className="w-full text-sm">
        <thead className="sticky top-0 bg-card"><tr className="text-left text-muted-foreground">
          {['#', 'Área', 'Estado', 'Tiempo', showAmount ? 'Total' : ''].map(h => <th key={h} className="font-semibold px-3 py-2">{h}</th>)}
        </tr></thead>
        <tbody>
          {units.map(u => (
            <tr key={u.unit_id} onClick={() => onSelect(u)} data-testid={`floor-list-row-${u.unit_id}`}
              className={`cursor-pointer border-t border-border ${selectedId === u.unit_id ? 'bg-[#2563EB]/10 outline outline-2 outline-[#2563EB] -outline-offset-2' : 'hover:bg-muted'}`}>
              <td className="px-3 py-2 font-bold">{u.name.replace(/^\D+/, '') || u.name}</td>
              <td className="px-3 py-2">{area(u.space_id)}</td>
              <td className="px-3 py-2"><span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ background: STATE_META[u.state]?.color }} />{STATE_META[u.state]?.label}</span></td>
              <td className="px-3 py-2 font-mono">{u.session_id ? fmtElapsed(u.opened_at, now) : u.reservation?.time || '-'}</td>
              {showAmount && <td className="px-3 py-2 font-mono">{u.total ? money(u.total) : '-'}</td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export const useFloorView = (serviceCount) => {
  const { highContrast } = useTheme();
  const mobile = typeof window !== 'undefined' && window.innerWidth < 768;
  const [view, setView] = useState(highContrast || mobile ? 'simple' : 'real');
  const many = serviceCount > ROWS_THRESHOLD;
  const fixed = many && view === 'list' ? 'rows' : !many && view === 'rows' ? 'list' : view;
  return { view: fixed, setView, many };
};

export const FloorPlan = ({ units, spaces, spaceId, view, now, selectedId, onSelect, onDoubleClick, editable, onMove, onPickDecor, pickedDecor, showAmount }) => {
  const decor = useMemo(() => units.filter(isDecor), [units]);
  const service = useMemo(() => fuseGroups(units.filter(u => !isDecor(u))), [units]);
  const shown = spaceId === 'all' ? spaces : spaces.filter(s => s.space_id === spaceId);
  const simple = view === 'simple';
  if (view === 'rows') {
    return <div className="p-4"><TableRowsGrid units={service} onSelect={onSelect} onDoubleClick={onDoubleClick} selectedId={selectedId} /></div>;
  }
  return (
    <div className="flex h-full min-h-[520px] rounded-2xl overflow-hidden border border-black/20 shadow-inner" data-testid="floor-plan">
      {view === 'list' && <UnitList units={service} spaces={spaces} selectedId={selectedId} onSelect={onSelect} now={now} showAmount={showAmount} />}
      <div className="flex flex-1 gap-1.5 bg-[#2b1d16] overflow-auto">
        {shown.map(s => (
          <SpaceCanvas key={s.space_id} space={s} showTag={shown.length > 1 || !simple}
            units={service.filter(u => u.space_id === s.space_id)} decor={decor.filter(d => d.space_id === s.space_id)}
            simple={simple} now={now} selectedId={selectedId} onSelect={onSelect} onDoubleClick={onDoubleClick}
            editable={editable} onMove={onMove} onPickDecor={onPickDecor} pickedDecor={pickedDecor} />
        ))}
      </div>
    </div>
  );
};
